import {
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
  type Timestamp,
} from 'firebase/firestore';

import { getSalaDb } from '../firebase/config';
import type { SalaTeacherProfile } from './types';

const COLLECTION = 'salaTeachers';

function toMillis(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (value && typeof value === 'object' && 'toMillis' in value) {
    try {
      return (value as Timestamp).toMillis();
    } catch {
      return Date.now();
    }
  }
  return Date.now();
}

export async function fetchSalaTeacherProfile(uid: string): Promise<SalaTeacherProfile | null> {
  const snap = await getDoc(doc(getSalaDb(), COLLECTION, uid));
  if (!snap.exists()) return null;
  const data = snap.data() as Record<string, unknown>;
  return {
    uid,
    displayName: String(data.displayName || '').trim(),
    email: String(data.email || '').trim(),
    subjectId: String(data.subjectId || '').trim(),
    subjectTag: String(data.subjectTag || '').trim(),
    schoolName: data.schoolName ? String(data.schoolName).trim() : undefined,
    createdAt: toMillis(data.createdAt),
    updatedAt: toMillis(data.updatedAt),
  };
}

export async function saveSalaTeacherProfile(
  input: Omit<SalaTeacherProfile, 'createdAt' | 'updatedAt'> & {
    createdAt?: number;
  },
): Promise<SalaTeacherProfile> {
  const ref = doc(getSalaDb(), COLLECTION, input.uid);
  const existing = await getDoc(ref);
  const now = Date.now();
  const payload = {
    displayName: input.displayName.trim(),
    email: input.email.trim(),
    subjectId: input.subjectId.trim(),
    subjectTag: input.subjectTag.trim(),
    schoolName: input.schoolName?.trim() || null,
    updatedAt: serverTimestamp(),
    ...(existing.exists() ? {} : { createdAt: serverTimestamp() }),
  };
  await setDoc(ref, payload, { merge: true });
  return {
    uid: input.uid,
    displayName: payload.displayName,
    email: payload.email,
    subjectId: payload.subjectId,
    subjectTag: payload.subjectTag,
    schoolName: payload.schoolName || undefined,
    createdAt: existing.exists()
      ? toMillis((existing.data() as Record<string, unknown>).createdAt) || now
      : now,
    updatedAt: now,
  };
}
